// Add a model (02-DESIGN/05-inference/01-models-and-instances.md § the
// console: Models): a form in the list's place (01-the-console.md
// § conventions: a form) — provider, the provider's model, the name,
// a display name, the API key, and an endpoint where the provider is
// self-hosted — that on save calls the three verbs the admin's role already
// admits: the workload is added with the install's inference image and the
// provider's host as what it may reach; the key is stored at the workload's
// own api-key path; the settings are set. The row then shows *starting*. The
// workspace's vault is placed at its creation (decision 0021), so storing the
// key waits for it only while it is still starting. The note beside the name: an
// Anthropic-API client keeps only names that begin with claude or anthropic.

import { useState } from "react";

import { CODE_NO_SERVICE, CODE_NOT_ADMITTED, isRefusal } from "../../platform/bus.ts";
import { Button, Choices, Field, NotAllowed, Notice, TextInput } from "../../ui/components.tsx";
import { FormPage } from "../../ui/form.tsx";
import { DOCUMENT_SETTINGS } from "../../wire/config.ts";
import {
  credentialPath,
  egressHost,
  passesAnthropicFilter,
  Provider,
  settingsText,
  validName,
  type Settings,
} from "../../wire/inference.ts";
import { registerSubject, SERVICE, type RegisterReply, type RegisterRequest } from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";

/** DEFAULT_RESOURCES is what a model runs with: the image's documented defaults are not published anywhere the console can read yet (hq issue 062), so the one size every host carries. */
export const DEFAULT_RESOURCES = { cpus: 1, memory_mib: 512 };

/** KEY_WAIT is how long storing the key waits for the workspace's vault to come up, in milliseconds; KEY_RETRY the pause between tries. */
export const KEY_WAIT = 150_000;
export const KEY_RETRY = 3_000;

type Step = "adding" | "vault" | "key" | "settings";

const STEP_TEXT: Record<Step, string> = {
  adding: "Adding the model…",
  vault: "Waiting for the workspace's vault to come up…",
  key: "Storing the key…",
  settings: "Saving the settings…",
};

const PROVIDERS: { value: string; label: string; hint: string }[] = [
  {
    value: Provider.openai,
    label: "OpenAI",
    hint: "OpenAI, or any server that speaks its API through an endpoint.",
  },
  { value: Provider.anthropic, label: "Anthropic", hint: "Anthropic's models through the Messages API." },
  {
    value: Provider.jev,
    label: "Jev",
    hint: "Decisions only: a state and questions in, answers out. Needs its endpoint.",
  },
];

/** egressRule renders the one rule the model's registration carries: the provider's host, and nothing else. */
export function egressRule(provider: string, baseURL: string): string | undefined {
  const h = egressHost(provider, baseURL === "" ? undefined : baseURL);
  return h === undefined ? undefined : `allow@${h.host}:tcp:${h.port}`;
}

export function AddModel({
  image,
  onClose,
  onAdded,
  onRegistered,
}: {
  /** The install's inference image reference, from the platform profile. */
  image: string;
  onClose: () => void;
  onAdded: (id: string) => void;
  /** onRegistered is told the moment the model's workload exists, so that the list shows it even if a later step fails. */
  onRegistered?: (id: string) => void;
}) {
  const s = useSurface();
  const [provider, setProvider] = useState<string>(Provider.openai);
  const [model, setModel] = useState("");
  const [name, setName] = useState("");
  const [display, setDisplay] = useState("");
  const [key, setKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [step, setStep] = useState<Step | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);
  const [partial, setPartial] = useState<string | undefined>(undefined);
  const [registeredRule, setRegisteredRule] = useState<string | undefined>(undefined);
  const [vaultLate, setVaultLate] = useState(false);
  const [moved, setMoved] = useState(false);

  const rule = egressRule(provider, endpoint.trim());
  const nameOK = validName(name.trim());
  const ready =
    model.trim() !== "" &&
    nameOK &&
    key !== "" &&
    rule !== undefined &&
    (provider !== Provider.jev || endpoint.trim() !== "");

  const submit = async () => {
    if (step !== undefined || !ready) {
      return;
    }
    setErr(undefined);
    setVaultLate(false);
    setMoved(false);
    setPartial(undefined);
    const settings: Settings = { provider, model: model.trim(), name: name.trim() };
    if (display.trim() !== "") {
      settings["display-name"] = display.trim();
    }
    if (endpoint.trim() !== "") {
      settings["base-url"] = endpoint.trim();
    }
    const body: RegisterRequest = {
      name: display.trim() !== "" ? display.trim() : name.trim(),
      description: `${provider} ${model.trim()}, asked for as ${name.trim()}`,
      image,
      resources: DEFAULT_RESOURCES,
      egress: [rule],
      // The name it answers discovery under, vouched in its attestation (decision 0026).
      names: [name.trim()],
    };
    // A retry after a later step failed resumes with the model already
    // added: registering again would add a second one.
    let id: string;
    if (partial !== undefined) {
      if (rule !== registeredRule) {
        setMoved(true);
        return;
      }
      id = partial;
    } else {
      setStep("adding");
      try {
        id = (await s.bus.call<RegisterReply>(SERVICE, registerSubject(), body, 60000)).id;
      } catch (e) {
        setStep(undefined);
        setErr(e);
        return;
      }
      setPartial(id);
      setRegisteredRule(rule);
      if (onRegistered !== undefined) {
        onRegistered(id);
      }
    }
    setStep("key");
    const until = Date.now() + KEY_WAIT;
    for (;;) {
      try {
        await s.bus.vault().store(credentialPath(id), key);
        break;
      } catch (e) {
        const vaultComing = isRefusal(e, CODE_NO_SERVICE) || isRefusal(e, CODE_NOT_ADMITTED);
        if (!vaultComing || Date.now() > until) {
          setStep(undefined);
          if (vaultComing) {
            setVaultLate(true);
          } else {
            setErr(e);
          }
          return;
        }
        setStep("vault");
        await new Promise((r) => setTimeout(r, KEY_RETRY));
      }
    }
    setStep("settings");
    try {
      await s.bus.configuration().set(id, DOCUMENT_SETTINGS, settingsText(settings));
    } catch (e) {
      setStep(undefined);
      setErr(e);
      return;
    }
    setKey("");
    setStep(undefined);
    onAdded(id);
  };

  return (
    <FormPage
      title="Add a model"
      back="Models"
      onClose={onClose}
      line={`A name ${s.name}'s apps and workloads ask for, and the provider's model that answers it.`}
      foot={
        <>
          {step !== undefined && <span className="small faint">{STEP_TEXT[step]}</span>}
          <Button kind="quiet" onClick={onClose} disabled={step !== undefined}>
            Cancel
          </Button>
          <Button
            kind="primary"
            onClick={() => {
              void submit();
            }}
            disabled={step !== undefined || !ready}
          >
            {step !== undefined ? "Adding…" : "Add"}
          </Button>
        </>
      }
    >
      <p className="muted">
        It runs for {s.name}, reaches the provider and nothing else, and shows as starting until it is ready.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Add" />}
      {vaultLate && (
        <Notice kind="bad" title="The vault is not up yet">
          {s.name}&apos;s vault did not answer in time, so the key is not stored. Add again in a minute: the
          model already added is kept.
        </Notice>
      )}
      {moved && (
        <Notice kind="bad" title="Where it reaches changed">
          The model was added reaching {registeredRule?.slice(registeredRule.indexOf("@") + 1)}. Put the
          provider and endpoint back, or open it and use Repoint.
        </Notice>
      )}
      {partial !== undefined && (
        <p className="small muted">
          The model exists but is not configured yet. Add again to finish, or open it under Models to store
          the key and the settings.{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              onAdded(partial);
            }}
          >
            Open it
          </button>
        </p>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <span className="label">Provider</span>
          <Choices name="provider" value={provider} onChange={setProvider} options={PROVIDERS} />
        </div>
        <Field label="The provider's model" hint="Its own id, sent to the provider as written.">
          <TextInput
            value={model}
            onChange={setModel}
            placeholder={
              provider === Provider.anthropic
                ? "claude-opus-5-5"
                : provider === Provider.openai
                  ? "gpt-5"
                  : ""
            }
            mono
            required
            autoFocus
          />
        </Field>
        <Field
          label="Name"
          hint={
            !nameOK && name.trim() !== ""
              ? "Letters, digits, dot, dash and underscore, up to 64."
              : name.trim() !== "" && !passesAnthropicFilter(name.trim())
                ? "Apps built against the Anthropic API drop from their pickers any model whose name does not begin with claude or anthropic. If your people use such apps, name it to pass that filter."
                : "What apps and workloads ask for. Pointing it at another model later changes nothing for them. Apps built against the Anthropic API keep only names beginning with claude or anthropic."
          }
        >
          <TextInput value={name} onChange={setName} placeholder="fast" mono required />
        </Field>
        <Field label="Display name" hint="What people read here and in a model picker; the name when empty.">
          <TextInput value={display} onChange={setDisplay} />
        </Field>
        <Field
          label="API key"
          hint="Typed once; stored in Secrets under the model's own path, readable by the model alone."
        >
          <TextInput value={key} onChange={setKey} type="password" required />
        </Field>
        <Field
          label="Endpoint"
          hint={
            provider === Provider.jev
              ? "Jev's endpoint; the model may reach its host and nothing else."
              : rule === undefined && endpoint.trim() !== ""
                ? "Not a URL."
                : `Only where the provider is self-hosted or regional. The model may reach ${
                    rule === undefined ? "its host" : rule.slice(rule.indexOf("@") + 1)
                  } and nothing else.`
          }
        >
          <TextInput value={endpoint} onChange={setEndpoint} type="url" mono placeholder="https://" />
        </Field>
      </form>
    </FormPage>
  );
}
